// ─────────────────────────────────────────────
//  useAlerts — send and listen to emergency/urgency alerts
// ─────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { listenToAlerts, sendAlert as fsendAlert } from "../firebase/firestore";
import { alertSentAt, isStaleAlert } from "../domain/alerts";

// How often the staleness of the alerts already on screen is re-checked.
//
// ⚠️ THE BOARD ON THE WALL IS NEVER RELOADED. Filtering only as snapshots
// arrive would leave an alert raised at 06:58 sitting there all of the next day
// on a display nobody touches, because no new snapshot has to arrive for it to
// go stale — only the clock has to move. Five minutes is far below the 07:00
// boundary it is watching for and costs one array filter.
const STALENESS_TICK_MS = 5 * 60 * 1000;

export function useAlerts(facilityId) {
  const [rawAlerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), STALENESS_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ⚠️ EXPIRY LIVES HERE, AT THE ONE DOOR EVERY ALERT COMES THROUGH. Nothing in
  // this app ever expired an alert: the query is facility+status only and the
  // status changes only when a human presses Resolve, so an urgency nobody
  // closed kept arriving on every board forever. On 2026-08-18 a WC E2 urgency
  // from 2026-06-30 was still rendering "Urgency active — acknowledge", 1167
  // hours old, and eleven such alerts were live on the facility.
  //
  // Filtered at the source rather than in each of the dozen surfaces that read
  // alerts, so no consumer can be forgotten and none of them has to know the
  // rule. See domain/alerts.isStaleAlert: an alert belongs to the OR day
  // (07:00→07:00) it was raised on.
  const alerts = useMemo(
    () => rawAlerts.filter((a) => !isStaleAlert(a, new Date(tick))),
    [rawAlerts, tick]
  );

  useEffect(() => {
    if (!facilityId) {
      // No facility (signed out / switching) — drop the previous facility's
      // alerts instead of letting them linger on screen.
      setAlerts([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = listenToAlerts(facilityId, (snapshot) => {
      const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest-first client-side (the query drops orderBy to avoid needing
      // a composite index).
      //
      // ⚠️ THROUGH alertSentAt, NOT `sentAt.toMillis()`. A live Firestore
      // Timestamp has toMillis, but a doc restored from cache or serialised
      // through storage arrives as a plain `{seconds, nanoseconds}` — and for
      // those the old expression fell to 0, so a whole cached board's alerts
      // sorted as if none of them had a time. alertSentAt already knows every
      // shape this app receives; the sorter and the expiry rule now read the
      // clock the same way.
      const ms = (a) => alertSentAt(a)?.getTime() || 0;
      rows.sort((a, b) => ms(b) - ms(a));
      setAlerts(rows);
      setLoading(false);
    }, (err) => {
      // A dropped/denied listener must not freeze the alert surfaces silently.
      console.error("[useAlerts] alerts listener failed:", err);
      setLoading(false);
    });
    return unsub;
  }, [facilityId]);

  // type: "emergency" | "urgency" | "break_request" | "room_condition"
  // targetIds: array of doctor UIDs to notify
  // roomName / senderName are denormalized so receivers render without a lookup.
  // break_request extras: giverId/giverName (who needs the break) and breakType
  // (canonical channel) MUST survive to the doc — the board card reads them to
  // mark exactly the requested break when someone covers.
  const sendNewAlert = async ({
    type, roomId, roomName = null, senderId, senderName = null,
    message, targetIds, targetType, roomDoctorId = null,
    giverId = null, giverName = null, breakType = null,
  }) => {
    await fsendAlert({
      facilityId, type, roomId, roomName, senderId, senderName,
      message, targetIds, targetType, roomDoctorId,
      giverId, giverName, breakType,
    });
  };

  return { alerts, loading, sendNewAlert };
}
